import { useMemo } from 'react'
import { isAfter } from 'date-fns'
import { theatricalGoalMilestones } from '@/constants/theatricalGoalMilestones'
import { getWebClient } from '@/services/ApolloClient'
import { useGoal } from '@/services/GoalService'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'
import { getDayBefore } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/utils/'

export interface GoalLineInfo {
  ticketsSold: number
  goalLineTitle: string
  milestonePercentage: number
  currentMilestone: number
  isLastMilestone: boolean
  kidTicketsSold: number
}

function isLastOfArray(element: number, array: number[]) {
  if (Array.isArray(array) && array.length > 0) {
    return element === array[array.length - 1]
  }
  return false
}

interface Props {
  releaseDate: string
  theatricalSlug: Maybe<string>
  isPossumTrotExperiment?: boolean
  usePifGoal?: boolean
}
const useTheatricalGoalService = ({ releaseDate, theatricalSlug, isPossumTrotExperiment, usePifGoal }: Props) => {
  const { t } = useTranslate('common')
  const client = getWebClient()
  const milestones = useMemo(() => {
    if (isPossumTrotExperiment) return [1000000]
    if (!theatricalSlug) return theatricalGoalMilestones.default
    return theatricalGoalMilestones[theatricalSlug] ?? theatricalGoalMilestones.default
  }, [isPossumTrotExperiment, theatricalSlug])

  const { raisedRaw } = useGoal({
    slug: theatricalSlug ?? '',
    client,
    usePifGoal,
  })

  const goalLineTitle = useMemo(() => {
    if (theatricalSlug === 'david') return 'Goal: 1 Million Tickets for 1 Million Kids'
    if (!releaseDate) return t('totalPreSales', 'Total Pre Sales')
    const isInBoxOffice = isAfter(new Date(), getDayBefore(new Date(releaseDate)))
    if (isInBoxOffice) return t('totalBoxOffice', 'Total Box Office')
    return t('totalPreSales', 'Total Pre Sales')
  }, [t, releaseDate, theatricalSlug])

  const goalLineInfo: GoalLineInfo = useMemo(() => {
    const ticketsSoldRaw = raisedRaw ? Math.round(raisedRaw / 100 / 14) : 0
    const ticketsSold = ticketsSoldRaw
    const currentUsGoalMilestone = milestones?.find((m: number, index: number) => {
      if (m > ticketsSold) return m
      if (milestones.length - 1 === index) return m
    })
    const currentMilestone = currentUsGoalMilestone ?? 0
    const hasMilestonePercentage = ticketsSold === 0 && currentMilestone === 0
    const milestonePercentage = hasMilestonePercentage ? 0 : Math.floor((ticketsSold / currentMilestone) * 100)
    // hardcoded 20% of goal tickets sold for now
    const kidTicketsSold = Math.round(ticketsSold * 0.2)

    // david override because the number of tickets sold is not accurate yet
    // TODO: remove david overrides once the number of tickets sold is accurate
    return {
      ticketsSold,
      goalLineTitle,
      milestonePercentage,
      isLastMilestone: isLastOfArray(currentMilestone, milestones),
      currentMilestone,
      kidTicketsSold: theatricalSlug === 'david' ? Math.max(kidTicketsSold, 20703) : kidTicketsSold,
    }
  }, [goalLineTitle, milestones, raisedRaw, theatricalSlug])

  return { goalLineInfo }
}

export default useTheatricalGoalService
